import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, Calendar, Download, RefreshCw, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import api from '../services/api';
import BarraDatos, { fmtBytes } from '../components/BarraDatos';

const DataStatsPro = () => {
  const [mode, setMode] = useState('range'); // range | monthly | total
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [start, setStart]     = useState(firstOfMonth.toISOString().slice(0, 10));
  const [end, setEnd]         = useState(today.toISOString().slice(0, 10));
  const [year, setYear]       = useState(today.getFullYear());
  const [data, setData]       = useState([]);
  const [totals, setTotals]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('bytes_total');
  const [sortDir, setSortDir] = useState('desc');

  const load = async () => {
    setLoading(true);
    try {
      if (mode === 'range') {
        const r = await api.get(`/api/stats/data/range?start=${start}&end=${end}`);
        setData(r.data.devices || []);
        setTotals(r.data.totals);
      } else if (mode === 'monthly') {
        const r = await api.get(`/api/stats/data/monthly?year=${year}`);
        setData(r.data || []);
        setTotals(null);
      } else {
        const r = await api.get('/api/stats/data');
        setData(r.data.devices || []);
        setTotals(r.data.totals);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [mode]);

  // Consumo segun Samsara. Va aparte del resto de la pagina a proposito: no sale
  // del contador del snap, sino del gateway, y las dos cifras juntas son el
  // diagnostico -- la diferencia es el trafico que el snap NO ve (video en vivo,
  // descargas y todo lo de los EPCOM/N9M).
  const [samsara, setSamsara] = useState(null);
  useEffect(() => {
    api.get('/api/samsara/data-usage').then(r => setSamsara(r.data)).catch(() => {});
  }, []);

  const sorted = useMemo(() => {
    const d = [...data];
    d.sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      if (va == null && vb == null) return 0;
      const cmp = (typeof va === 'number' && typeof vb === 'number')
        ? va - vb : String(va || '').localeCompare(String(vb || ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return d;
  }, [data, sortKey, sortDir]);

  const fmt = (n) => {
    if (!n) return '0 B';
    const u = ['B','KB','MB','GB','TB'];
    let i = 0;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(2)} ${u[i]}`;
  };

  const exportCSV = () => {
    const rows = mode === 'monthly'
      ? [['device_id','name','year','month','recibido','enviado','total','snapshots']]
      : [['device_id','name','recibido','enviado','total']];
    sorted.forEach(r => {
      if (mode === 'monthly') {
        rows.push([r.device_id, r.name, r.year, r.month, r.bytes_received,
                   r.bytes_sent, r.bytes_total, r.snapshots_count]);
      } else {
        rows.push([r.device_id, r.name, r.bytes_received, r.bytes_sent, r.bytes_total]);
      }
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `consumo_${mode}_${Date.now()}.csv`; a.click();
  };

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ArrowUpDown size={12} style={{ opacity: 0.4 }} />;
    return sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  // Quick presets
  const setPreset = (preset) => {
    const t = new Date();
    let s;
    if (preset === 'today') s = t;
    else if (preset === '7d') { s = new Date(); s.setDate(t.getDate() - 7); }
    else if (preset === '30d') { s = new Date(); s.setDate(t.getDate() - 30); }
    else if (preset === 'mtd') s = new Date(t.getFullYear(), t.getMonth(), 1);
    else if (preset === 'lastmonth') {
      s = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const e = new Date(t.getFullYear(), t.getMonth(), 0);
      setStart(s.toISOString().slice(0, 10));
      setEnd(e.toISOString().slice(0, 10));
      return;
    }
    setStart(s.toISOString().slice(0, 10));
    setEnd(t.toISOString().slice(0, 10));
  };

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><BarChart3 size={26} /> Consumo de Datos</h1>
          <p style={S.subtitle}>{sorted.length} dispositivos</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btn} onClick={exportCSV}><Download size={16}/> CSV</button>
          <button style={S.btn} onClick={load}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} /> Actualizar
          </button>
        </div>
      </header>

      <div style={S.tabs}>
        {[
          { k: 'range',   l: 'Rango fechas' },
          { k: 'monthly', l: 'Histórico mensual' },
          { k: 'total',   l: 'Acumulado total' },
        ].map(t => (
          <button key={t.k} onClick={() => setMode(t.k)}
                  style={{ ...S.tab, ...(mode === t.k ? S.tabActive : {}) }}>{t.l}</button>
        ))}
      </div>

      {mode === 'range' && (
        <div style={S.filters}>
          <div style={S.dateGroup}>
            <Calendar size={16} />
            <input type="date" value={start} onChange={e => setStart(e.target.value)} style={S.dateInput}/>
            <span>→</span>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} style={S.dateInput}/>
            <button style={S.btnPrimary} onClick={load}>Aplicar</button>
          </div>
          <div style={S.presets}>
            <button style={S.preset} onClick={() => setPreset('today')}>Hoy</button>
            <button style={S.preset} onClick={() => setPreset('7d')}>7 días</button>
            <button style={S.preset} onClick={() => setPreset('30d')}>30 días</button>
            <button style={S.preset} onClick={() => setPreset('mtd')}>Este mes</button>
            <button style={S.preset} onClick={() => setPreset('lastmonth')}>Mes pasado</button>
          </div>
        </div>
      )}

      {mode === 'monthly' && (
        <div style={S.filters}>
          <label>Año:&nbsp;</label>
          <select value={year} onChange={e => { setYear(+e.target.value); }} style={S.select}>
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={S.btnPrimary} onClick={load}>Aplicar</button>
        </div>
      )}

      {totals && (
        <div style={S.totals}>
          <div style={S.totalCard}>
            <span style={S.totalLab}>Recibido</span>
            <span style={S.totalVal}>{totals.bytes_received_formatted}</span>
          </div>
          <div style={S.totalCard}>
            <span style={S.totalLab}>Enviado</span>
            <span style={S.totalVal}>{totals.bytes_sent_formatted}</span>
          </div>
          <div style={{ ...S.totalCard, background: '#3b82f6', color: 'white' }}>
            <span style={{ ...S.totalLab, color: '#dbeafe' }}>Total</span>
            <span style={S.totalVal}>{totals.bytes_total_formatted}</span>
          </div>
        </div>
      )}

      {samsara && (samsara.unidades?.length > 0 || samsara.aviso) && (
        <div style={S.tableWrap}>
          <div style={SX.cabecera}>
            <div>
              <b>Hotspot Samsara</b>
              <span style={SX.nota}>
                {' '}del ciclo del plan, que corta el día 1. Al 100% Samsara restringe
                el hotspot a su propia app, el MDVR se queda sin internet y el snap le
                impone la cadencia lenta. En gris = sin lectura fresca: ese gateway dejó
                de reportar y la cifra puede ser del ciclo anterior.
              </span>
            </div>
            {samsara.agotadas > 0 && (
              <span style={SX.agotadas}>{samsara.agotadas} al tope</span>
            )}
          </div>
          {samsara.aviso && <div style={SX.aviso}>{samsara.aviso}</div>}
          {samsara.unidades?.length > 0 && (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Unidad</th>
                  <th style={S.th}>Hotspot (ciclo)</th>
                  <th style={S.th}>Consumo del plan</th>
                  <th style={S.th}>El snap midió (mes)</th>
                  <th style={S.th} title="Lo que Samsara ve y el snap no: video en vivo, descargas, EPCOM/N9M">
                    No visto por el snap
                  </th>
                  <th style={S.th}>Celular del gateway</th>
                </tr>
              </thead>
              <tbody>
                {[...samsara.unidades].sort((a, b) => b.hotspot_bytes - a.hotspot_bytes)
                  .map(u => (
                  <tr key={u.device_id || u.asset_name}>
                    <td style={S.td}>
                      <b>{u.asset_name || u.device_name || u.device_id}</b>
                      <div style={SX.mono}>{u.device_id}</div>
                    </td>
                    <td style={{ ...S.td, color: u.lectura_vieja ? '#9ca3af' : undefined }}>
                      {fmtBytes(u.hotspot_bytes)}
                    </td>
                    <td style={S.td}>
                      <BarraDatos usados={u.hotspot_bytes} plan={u.plan_bytes}
                                  vieja={u.lectura_vieja} edadMin={u.edad_min} />
                    </td>
                    <td style={S.td}>{u.snap_bytes_mes == null ? '—' : fmtBytes(u.snap_bytes_mes)}</td>
                    <td style={{ ...S.td, color: (u.diferencia_bytes || 0) > 50 * 1048576 ? '#dc2626' : '#6b7280' }}>
                      {u.diferencia_bytes == null ? '—' : fmtBytes(u.diferencia_bytes)}
                    </td>
                    <td style={{ ...S.td, color: '#6b7280' }}>{fmtBytes(u.cellular_bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {samsara.sin_cuadrar?.length > 0 && (
            <div style={SX.sinCuadrar}>
              {samsara.sin_cuadrar.length} asset(s) de Samsara sin unidad en el snap
              (remolques o nombres que no cuadran):{' '}
              {samsara.sin_cuadrar.slice(0, 8).map(x => x.asset_name).join(', ')}
              {samsara.sin_cuadrar.length > 8 ? '…' : ''}
            </div>
          )}
        </div>
      )}

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <Th onClick={() => toggleSort('name')}>Dispositivo <SortIcon k="name"/></Th>
              {mode === 'monthly' && <Th onClick={() => toggleSort('year')}>Año <SortIcon k="year"/></Th>}
              {mode === 'monthly' && <Th onClick={() => toggleSort('month')}>Mes <SortIcon k="month"/></Th>}
              <Th onClick={() => toggleSort('bytes_received')}>Recibido <SortIcon k="bytes_received"/></Th>
              <Th onClick={() => toggleSort('bytes_sent')}>Enviado <SortIcon k="bytes_sent"/></Th>
              <Th onClick={() => toggleSort('bytes_total')}>Total <SortIcon k="bytes_total"/></Th>
              {(mode === 'range' || mode === 'monthly') &&
                <Th onClick={() => toggleSort('snapshots_count')}>Snaps <SortIcon k="snapshots_count"/></Th>}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={7} style={S.empty}>Sin datos en este rango</td></tr>
            )}
            {sorted.map((r, i) => (
              <tr key={`${r.device_id}-${r.year || ''}-${r.month || ''}-${i}`}>
                <td style={{ ...S.td, fontWeight: 600 }}>{r.name || r.device_id}</td>
                {mode === 'monthly' && <td style={S.td}>{r.year}</td>}
                {mode === 'monthly' && <td style={S.td}>{String(r.month).padStart(2, '0')}</td>}
                <td style={S.td}>{fmt(r.bytes_received)}</td>
                <td style={S.td}>{fmt(r.bytes_sent)}</td>
                <td style={{ ...S.td, fontWeight: 600 }}>{fmt(r.bytes_total)}</td>
                {(mode === 'range' || mode === 'monthly') &&
                  <td style={S.td}>{r.snapshots_count ?? '-'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const Th = ({ children, onClick }) => (
  <th onClick={onClick} style={S.th}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
  </th>
);

const S = {
  container: { padding: 24, maxWidth: 1300, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  btn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px',
         background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' },
  btnPrimary: { padding: '8px 16px', background: '#3b82f6', color: 'white',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 },

  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb',
         background: 'white', cursor: 'pointer', fontSize: 14 },
  tabActive: { background: '#3b82f6', color: 'white', borderColor: '#3b82f6' },

  filters: { background: 'white', padding: 14, borderRadius: 8, marginBottom: 16,
             display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  dateGroup: { display: 'flex', alignItems: 'center', gap: 8 },
  dateInput: { padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14 },
  presets: { display: 'flex', gap: 6, marginLeft: 'auto' },
  preset: { padding: '6px 12px', background: '#f3f4f6', border: 'none',
            borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  select: { padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14 },

  totals: { display: 'flex', gap: 12, marginBottom: 16 },
  totalCard: { background: 'white', padding: 16, borderRadius: 8, flex: 1,
               display: 'flex', flexDirection: 'column', gap: 4,
               boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  totalLab: { fontSize: 12, color: '#6b7280' },
  totalVal: { fontSize: 22, fontWeight: 700 },

  tableWrap: { background: 'white', borderRadius: 8, overflow: 'auto',
               boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { padding: '12px 14px', textAlign: 'left', background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb', cursor: 'pointer', userSelect: 'none',
        fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase' },
  td: { padding: '12px 14px', borderBottom: '1px solid #f3f4f6' },
  empty: { padding: 40, textAlign: 'center', color: '#9ca3af' },
};

const SX = {
  cabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              gap: 12, padding: '10px 12px', borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb', fontSize: 13 },
  nota: { color: '#6b7280', fontSize: 12 },
  agotadas: { background: '#fee2e2', color: '#991b1b', fontWeight: 700,
              fontSize: 12, padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap' },
  aviso: { padding: '10px 12px', fontSize: 13, color: '#92400e', background: '#fef3c7' },
  mono: { fontFamily: 'monospace', fontSize: 11, color: '#9ca3af' },
  sinCuadrar: { padding: '8px 12px', fontSize: 12, color: '#6b7280',
                borderTop: '1px solid #e5e7eb', background: '#f9fafb' },
};

export default DataStatsPro;
